import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Play, Pause, RotateCcw } from "lucide-react";
import ScoringEngine from "@/components/ScoringEngine";

export default function LiveGameTracking() {
  const { tournamentId, gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState(null);
  const [initialGame, setInitialGame] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    api.get(`/tournaments/${tournamentId}/games/${gameId}`)
      .then((response) => {
        if (!alive) return;
        setGameData(response.data);
      })
      .catch((error) => {
        if (!alive) return;
        console.error("[LiveGameTracking] load failed", error);
        toast.error(error?.response?.data?.detail || "Unable to load live game data");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [tournamentId, gameId]);

  useEffect(() => {
    if (!gameData) return;
    const defaultGame = {
      quarter: 1,
      clock: 720,
      running: false,
      timeoutsA: 4,
      timeoutsB: 4,
      foulsA: 0,
      foulsB: 0,
      teamA: {
        name: gameData.home_team.name,
        players: (gameData.home_team.players || []).map((player, idx) => ({
          id: player.id,
          name: player.name || "Unnamed",
          number: player.jersey_number || idx + 1,
          stats: {
            points: 0,
            rebounds_off: 0,
            rebounds_def: 0,
            assists: 0,
            steals: 0,
            blocks: 0,
            turnovers: 0,
            fouls: 0,
            shots: [],
          },
        })),
      },
      teamB: {
        name: gameData.away_team.name,
        players: (gameData.away_team.players || []).map((player, idx) => ({
          id: player.id,
          name: player.name || "Unnamed",
          number: player.jersey_number || idx + 1,
          stats: {
            points: 0,
            rebounds_off: 0,
            rebounds_def: 0,
            assists: 0,
            steals: 0,
            blocks: 0,
            turnovers: 0,
            fouls: 0,
            shots: [],
          },
        })),
      },
      activePlayerId: gameData.home_team.players?.[0]?.id || "",
      log: [],
    };

    setInitialGame(defaultGame);
  }, [gameData]);

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Loading live game tracking…</div>;
  }

  if (!gameData) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Game not found.</div>;
  }

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <div className="bg-charcoal border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-[10px] uppercase tracking-[0.3em] text-court">Live Game Tracking</div>
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white">
            <RotateCcw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {initialGame ? (
        <ScoringEngine initialGame={initialGame} storageKey={`hm_live_game_${gameId}`} />
      ) : (
        <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Preparing live game roster…</div>
      )}
    </div>
  );
}
